import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { Cog8ToothIcon } from '@heroicons/react/24/outline';
import storeApi from '../../services/storeApi';
import Notification from '../../components/notification/notification';
import { Upload } from 'antd';
import InputMask from 'react-input-mask';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AddressAutocomplete from '../../components/address-autocomplete/address-autocomplete';
import moment from 'moment/moment';
import StoreIcon from '../../assets/store-icon.svg';
import { LogoOrMonogram } from '../../components/company-changer/monogram';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import getAvailability from '../../utils/getAvailability';
import StoresSkeleton from './components/stores-skeleton';
import {
  PageHeader,
  TWModal,
  TWButton,
  TWInput,
  FieldLabel,
} from '../../components/ui';
import './stores.css';

const utc = require('dayjs/plugin/utc');
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.Ls.en.weekStart = 1;

const cn = (...c) => c.filter(Boolean).join(' ');

const fieldClass =
  'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400';

const emptyForm = (companyid) => ({
  companyid,
  latitude: 0,
  longitude: 0,
  city: '',
  state: '',
  postalcode: '',
  address: '',
  name: '',
  id: '',
  terms: true,
  tax: 0,
  responsiblename: '',
  responsiblephone: '',
  customerfeeactive: false,
  customerfeeisrate: true,
  tipactive: true,
  customernoteactive: true,
  customernameactive: true,
  tablenumberactive: false,
  customerfee: 0,
  selectedtip: 0,
  phone: '',
  apartment: '',
  tiprate1: 25,
  tiprate2: 20,
  tiprate3: 15,
  tiprate4: 10,
  tiprate5: 0,
  timezone: moment().utcOffset() / 60,
});

const Stores = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);

  const [form, setForm] = useState(() => emptyForm(globalCompany.id));
  const [errors, setErrors] = useState({
    name: false,
    responsiblename: false,
    responsiblephone: false,
    address: false,
    phone: false,
  });
  const [list, setList] = useState([]);
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
    loginAsCompany: false,
  });
  const [dialogs, setDialogs] = useState({
    userList: false,
    upsert: false,
    delete: false,
  });
  const cancelButtonRef = useRef(null);

  const getList = async () => {
    try {
      setLoading((prev) => ({ ...prev, listLoading: true }));
      const query = globalCompany.id ? `companyid=${globalCompany.id}` : '';
      const res = await storeApi.list(query);
      setList(res.data.data?.length > 0 ? res.data.data : []);
    } catch (error) {
      setList([]);
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id]);

  const onClickAddStore = () => {
    setForm(emptyForm(globalCompany.id));
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormChange = (val, key) => {
    if (key === 'name') {
      // Allow punctuation common in business names (e.g. "Sam's Kebob & Gyro's - Mediterranean Grill").
      const numAndLetters = /^[A-Za-z0-9 _'’&.,()/-]*$/;
      setForm((prev) => ({
        ...prev,
        [key]: numAndLetters.test(val) ? val : prev[key],
      }));
    } else {
      setForm((prev) => ({ ...prev, [key]: val }));
    }
  };

  const onFormSubmit = async () => {
    if (
      !form.name ||
      !form.responsiblename ||
      !form.responsiblephone ||
      !form.address ||
      !form.phone
    ) {
      setErrors({
        name: !form.name,
        responsiblename: !form.responsiblename,
        responsiblephone: !form.responsiblephone,
        address: !form.address,
        phone: !form.phone,
      });
      return;
    }
    setErrors({
      name: false,
      responsiblename: false,
      responsiblephone: false,
      address: false,
      phone: false,
    });
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...form, websiteclosedat: form.websiteclosedat || '' };
      const formData = new FormData();

      for (const key of Object.keys(body)) {
        if (key === 'storelogo' && body.storelogo !== '') {
          if (body.storelogoid) {
            await storeApi.deleteStoreLogoById(body.id);
          }
          formData.append('storelogo', body.storelogo);
        } else {
          formData.append(key, body[key]);
        }
      }

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const res = await storeApi.upsert(formData, config);
      if (res.data.data) {
        setDialogs((prev) => ({ ...prev, upsert: false }));
      }
    } catch (error) {
      // swallow
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getList();
    }
  };

  const onCloseUpsert = () => {
    setErrors({
      address: false,
      name: false,
      responsiblename: false,
      responsiblephone: false,
      phone: false,
    });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onAddressSelect = (obj) => {
    setForm((prev) => ({
      ...prev,
      address: obj.street,
      city: obj.city,
      state: obj.state,
      postalcode: obj.zipCode,
      longitude: obj?.longitude || '',
      latitude: obj?.latitude || '',
      apartment: obj?.apartment || '',
    }));
  };

  const uploadButton = (
    <div>
      {loading.upsertLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div className="dark:text-slate-500" style={{ marginTop: 8 }}>
        Upload
      </div>
    </div>
  );

  return (
    <div className="stores-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <header className="reveal flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-600 dark:bg-slate-800 sm:flex">
              <img src={StoreIcon} alt="" className="h-9 w-9" />
            </div>
            <PageHeader
              title="Stores"
              description="Create locations and manage hours, branding, and settings for each store."
              className="!gap-1"
            />
          </div>
        </header>

        {loading.listLoading ? (
          <StoresSkeleton />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((store, storeIdx) => {
              const avail = getAvailability(store);
              const badgeClass =
                avail.status === 'open'
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : avail.status === 'opensSoon'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                    : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300';
              return (
                <div
                  key={storeIdx}
                  className="reveal group relative overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
                  style={{ animationDelay: `${storeIdx * 60}ms` }}
                >
                  <Link
                    to={`${store.id}/settings`}
                    className="absolute right-2 top-2 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Store settings"
                  >
                    <Cog8ToothIcon className="h-5 w-5" aria-hidden="true" />
                  </Link>
                  <Link className="block h-full" to={`${store.id}`}>
                    <div className="flex flex-col items-center p-6 text-center">
                      <LogoOrMonogram
                        src={store.storelogo}
                        name={store.name}
                        size={64}
                        imgClassName="rounded-md object-cover ring-2 ring-white shadow-sm dark:ring-slate-700"
                      />
                      <h2 className="mt-3 line-clamp-2 text-base font-bold text-slate-900 dark:text-slate-100">
                        {store.name}
                      </h2>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
                        {store.address}
                      </p>
                      <span
                        className={cn(
                          'mt-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                          badgeClass,
                        )}
                      >
                        {avail.label}
                      </span>
                    </div>
                  </Link>
                </div>
              );
            })}
            <button
              type="button"
              onClick={onClickAddStore}
              className="reveal flex min-h-[220px] flex-col items-center justify-center rounded-md border-2 border-dashed border-slate-200 bg-white/80 p-8 text-center transition hover:border-blue-400 hover:bg-white hover:shadow-md dark:border-slate-600 dark:bg-slate-800/50 dark:hover:border-sky-500 dark:hover:bg-slate-800"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10 text-blue-600 dark:bg-sky-400/10 dark:text-sky-400">
                <PlusIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                Create a new store
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Add another location to your account
              </p>
            </button>
          </div>
        )}
      </div>

      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title="Add store"
        description="Create a new location and configure it later from settings."
        size="2xl"
        initialFocus={cancelButtonRef}
        showCloseButton
        bodyClassName="max-h-[65vh] overflow-y-auto"
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <TWButton
              ref={cancelButtonRef}
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={onCloseUpsert}
            />
            <TWButton
              variant="primary"
              label={loading.upsertLoading ? 'Saving…' : 'Add store'}
              loading={loading.upsertLoading}
              onClick={onFormSubmit}
            />
          </div>
        }
      >
        <div className="grid grid-cols-8 gap-6">
          <div className="col-span-8">
            <FieldLabel>Store Logo</FieldLabel>
            <div className="mt-3">
              <Upload
                multiple={false}
                maxCount={1}
                name="avatar"
                onChange={(e) => onFormChange(e.file, 'storelogo')}
                listType="picture-circle"
                className="avatar-uploader"
                beforeUpload={() => false}
              >
                {form.storelogo ? (
                  <img
                    src={form.storelogo}
                    alt="avatar"
                    className="min-h-[4rem] min-w-[4rem] h-16 w-16 rounded-full"
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
              {errors.imageid && (
                <p className="mt-1 text-xs text-rose-600">
                  Image can not be empty
                </p>
              )}
            </div>
          </div>

          <div className="col-span-8">
            <TWInput
              label="Store Name"
              required
              value={form.name}
              onChange={(e) => onFormChange(e.target.value, 'name')}
              placeholder="e.g. 'ABC Pizza'"
              error={errors.name ? 'Store name can not be empty' : undefined}
            />
          </div>

          <div className="col-span-8 sm:col-span-4">
            <FieldLabel htmlFor="phone" required>
              Store Phone Number
            </FieldLabel>
            <InputMask
              value={form.phone}
              mask="(999) 999 9999"
              maskPlaceholder={' '}
              onChange={(e) => onFormChange(e.target.value, 'phone')}
              type="text"
              name="phone"
              id="phone"
              className={fieldClass}
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-rose-600">
                Store phone can not be empty
              </p>
            )}
          </div>

          <div className="col-span-8 sm:col-span-4">
            <TWInput
              label="Sales Tax %"
              required
              type="number"
              value={form.tax}
              onChange={(e) => onFormChange(e.target.value, 'tax')}
              placeholder="0"
            />
          </div>

          <div className="col-span-8">
            <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
              Store address
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Your address will be displayed publicly.
            </p>
          </div>

          <div className="col-span-8 sm:col-span-6">
            <FieldLabel htmlFor="address">Street Name</FieldLabel>
            <AddressAutocomplete
              value={form.address}
              onChange={(e) => onAddressSelect(e)}
            />
            <p
              className={`mt-1 text-xs ${errors.address ? 'text-rose-600' : 'text-slate-500 dark:text-slate-500'}`}
            >
              {errors.address
                ? 'Address can not be empty'
                : `Please type your street name here e.g. '100 Main St'`}
            </p>
          </div>

          <div className="col-span-8 sm:col-span-2">
            <TWInput
              label="Apt / Suite"
              value={form.apartment}
              onChange={(e) => onFormChange(e.target.value, 'apartment')}
            />
          </div>

          <div className="col-span-8 grid grid-cols-8 gap-6">
            <div className="col-span-8 sm:col-span-4">
              <TWInput
                label="City"
                value={form.city}
                onChange={(e) => onFormChange(e.target.value, 'city')}
              />
            </div>
            <div className="col-span-8 sm:col-span-2">
              <TWInput
                label="State"
                value={form.state}
                onChange={(e) => onFormChange(e.target.value, 'state')}
              />
            </div>
            <div className="col-span-8 sm:col-span-2">
              <TWInput
                label="Zip Code"
                value={form.postalcode}
                onChange={(e) => onFormChange(e.target.value, 'postalcode')}
              />
            </div>
          </div>

          <div className="col-span-8">
            <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
              Store manager
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              This information <strong>will not</strong> be displayed publicly.
            </p>
          </div>

          <div className="col-span-8 sm:col-span-4">
            <TWInput
              label="Manager's Full Name"
              value={form.responsiblename}
              onChange={(e) => onFormChange(e.target.value, 'responsiblename')}
              placeholder="e.g. 'John Doe'"
              error={
                errors.responsiblename
                  ? "Manager's full name can not be empty"
                  : undefined
              }
            />
          </div>

          <div className="col-span-8 sm:col-span-4">
            <FieldLabel htmlFor="responsiblephone">
              Manager's Phone Number
            </FieldLabel>
            <InputMask
              value={form.responsiblephone}
              mask="(999) 999 9999"
              maskPlaceholder={' '}
              onChange={(e) => onFormChange(e.target.value, 'responsiblephone')}
              type="text"
              name="responsiblephone"
              id="responsiblephone"
              className={fieldClass}
            />
            {errors.responsiblephone && (
              <p className="mt-1 text-xs text-rose-600">
                Manager's phone number can not be empty
              </p>
            )}
          </div>
        </div>
      </TWModal>

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default Stores;
